import { type StudySummary, studyFacets } from "@ir/contracts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/PageHeader";
import { CategoryPills } from "@/features/catalog/CategoryPills";
import { FeaturedSection } from "@/features/catalog/FeaturedSection";
import { StudyCard } from "@/features/catalog/StudyCard";
import { useStudies } from "@/features/catalog/useStudies";
import { apiFetch } from "@/lib/api";

export const Route = createFileRoute("/_auth/insights/")({ component: CatalogPage });

function CatalogPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"recent" | "title">("recent");
  const [category, setCategory] = useState<string | null>(null);

  // Difiere la consulta para no lanzar una petición por cada tecla.
  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError } = useStudies({
    q: deferredSearch || undefined,
    category: category ?? undefined,
    sort,
    pageSize: 24,
  });

  const featured = useStudies({ featured: true, pageSize: 4, sort: "recent" });
  const showFeatured = !deferredSearch && (featured.data?.items.length ?? 0) > 0;
  // Las categorías de los insights que esta persona puede ver: una pill de
  // una categoría sin insights visibles solo llevaría a un listado vacío.
  const facets = useQuery({
    queryKey: ["facets"],
    queryFn: () => apiFetch("/api/studies/facets", { schema: studyFacets }),
  });
  const categories = facets.data?.categories ?? [];

  return (
    <>
      <PageHeader
        title={t("catalog.title")}
        subtitle={t("catalog.subtitle")}
        actions={
          <>
            <div className="relative">
              <Search
                size={13}
                className="absolute top-1/2 left-2.5 -translate-y-1/2 text-ink4"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                placeholder={t("catalog.search")}
                aria-label={t("catalog.search")}
                className="h-9 w-[240px] rounded-[3px] border border-line bg-card pr-3 pl-8 text-[13px] placeholder:text-ink4 focus-visible:border-bur focus-visible:outline-none"
              />
            </div>

            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as "recent" | "title");
              }}
              aria-label={t("catalog.sortRecent")}
              className="h-9 rounded-[3px] border border-line bg-card px-2 text-[13px] text-ink2"
            >
              <option value="recent">{t("catalog.sortRecent")}</option>
              <option value="title">{t("catalog.sortTitle")}</option>
            </select>
          </>
        }
      />

      <div className="px-8 py-6">
        {showFeatured && featured.data && (
          <FeaturedSection>
            <StudyGrid studies={featured.data.items} />
          </FeaturedSection>
        )}

        {(showFeatured || categories.length > 0) && (
          <h2 className="mb-3 text-ink2">{t("catalog.all")}</h2>
        )}
        <CategoryPills categories={categories} selected={category} onSelect={setCategory} />

        {isPending && <p className="text-nota text-ink3">{t("common.loading")}</p>}
        {isError && <p className="text-nota text-warn">{t("common.error")}</p>}

        {data && data.items.length === 0 && (
          <p className="py-16 text-center text-nota text-ink3">
            {deferredSearch
              ? t("catalog.emptySearch")
              : category
                ? t("catalog.emptyCategory")
                : t("catalog.empty")}
          </p>
        )}

        {data && data.items.length > 0 && <StudyGrid studies={data.items} />}
      </div>
    </>
  );
}

function StudyGrid({ studies }: { studies: StudySummary[] }) {
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {studies.map((study) => (
        <li key={study.id}>
          <StudyCard study={study} />
        </li>
      ))}
    </ul>
  );
}
